import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import SectionCard from 'components/section/SectionCard';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { Button, Form, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { hasGlKey } from '../utils/helperFunc';
import { useRef, useEffect, useMemo, useState } from 'react';
import LeaseModifyScheduleTable from './LeaseModifyScheduleTable';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';

dayjs.extend(utc);

const LeaseRecordManagementV9ModifySchedule = ({ screen = 'revise', checkFormValidation, viewData, dataFullyLoaded = false }) => {
	const { state } = useLocation();
	const form = Form.useFormInstance();
	const hasLoadedOnce = useRef(false);

	const [isClicked, setIsClicked] = useState(false);
	const [hasLoadedData, setHasLoadedData] = useState(false);
	const [selectedStatuses, setSelectedStatuses] = useState([]);
	const [filteredDataForTable, setFilteredDataForTable] = useState([]);
	const [memoizedLeaseControlData, setMemoizedLeaseControlData] = useState([]);
	const [flag, setFlag] = useState({
		check: 0,
		editable: 0,
	});

	const companyCode = useSelector((state) => state.profile.profileData?.creatorOrganizationId);

	const [getLeaseTableData, { data: leaseControlDataV3, isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();
	const [getLeaseData, { data: leaseControlData, isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'view'
			? Object.keys(viewData?.parkedData ?? {})?.length === 0
				? viewData?.data
				: viewData?.parkedData
			: [];
	}, [viewData, screen]);

	const hasGlAssigned = hasGlKey(statusData);
	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);

	const isV3DataActive = useMemo(
		() => (screen?.toLowerCase() === 'view' || flag?.check === 0) && viewData?.activationStatus?.toLowerCase() === 'active',
		[screen, flag, viewData]
	);

	const queryV3Loader = async (force = false) => {
		let result;

		if (screen !== 'CREATE' && (!hasLoadedData || force)) {
			const version = String(viewData?.data?.version);

			if (viewData?.activationStatus?.toLowerCase() !== 'active') {
				await handleDataLoading();
				return;
			}

			if (
				version &&
				version !== 'null' &&
				version !== 'undefined' &&
				companyCode
				// &&
				// (viewData?.activationStatus?.toLowerCase() === 'active' ||
				// 	viewData?.activationStatus?.toLowerCase() === 'closed' ||
				// 	viewData?.activationStatus?.toLowerCase() === 're-open')
			) {
				result = await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, version],
					name: 'leaseSchedulesOnVersion-v9',
				});
			}

			setHasLoadedData(true);

			return result;
		}
	};

	const handleDataLoading = async () => {
		if (hasLoadedOnce.current) return;
		hasLoadedOnce.current = true;

		setIsClicked(true);

		let payloadData = {};

		const formData = form.getFieldsValue();

		if (screen?.toLowerCase() === 'create' || (screen?.toLowerCase() === 'revise' && formData?.modificationSequence === 0)) {
			payloadData = {
				advanceAmount: formData?.advanceAmount_new,
				leaseTerm: formData?.leaseTerm,
				discountRate: formData?.initialDiscountRate_new,
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				paymentFrequency: formData?.paymentFrequency_new,
				paymentTime: formData?.paymentModality_new,
				advanceDuration: formData?.advanceAmounts_new?.filter((item) => item !== 0)?.length,
				startDay: String(formData?.leaseStartDate?.date()),
				upfrontLeasePayment: String(formData?.upfrontLeasePayment_new),
				initialDirectCost: String(formData?.initialDirectCost_new),
				securityDeposit: String(formData?.securityDeposit_new),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.advanceAmounts_new,
				grossRents: formData?.grossRents_new,
				grossRent: 0,
				changes: [],
				cashIncentiveAmount: String(formData?.cashIncentiveAmount_new ?? 0),
			};
			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};
			try {
				if (Object.keys(payloadData).length !== 0) {
					await getLeaseData(formattedPayload).unwrap();
				}
			} catch (error) {
				return;
			}
		} else {
			if (dataFullyLoaded) {
				const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);

				const normalizedEffectiveChanges = effectiveChanges?.map((item) => {
					const grossRentsLength = item?.inputChangeGrossRents?.length || 0;
					const advanceAmountsLength = item?.inputChangeAdvanceAmounts?.length || 0;

					if (grossRentsLength > advanceAmountsLength && Array.isArray(item?.inputChangeGrossRents)) {
						const paddedAdvanceAmounts = [...(item?.inputChangeAdvanceAmounts || [])];

						while (paddedAdvanceAmounts.length < grossRentsLength) {
							paddedAdvanceAmounts.push(0);
						}
						return {
							...item,
							inputChangeAdvanceAmounts: paddedAdvanceAmounts,
						};
					}
					return item;
				});

				payloadData = {
					advanceAmount: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce(
						(accumulator, currentValue) => accumulator + currentValue,
						0
					),
					leaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
					discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
					startDay: String(formData?.leaseStartDate?.date()),
					startMonth: String(formData?.leaseStartDate?.month() + 1),
					startYear: String(formData?.leaseStartDate?.year()),
					escalation: 0,
					paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
					paymentTime: formData?.changes?.[0]?.paymentTime,
					advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
					upfrontLeasePayment: formData?.changes?.[0]?.upfrontLeasePayment,
					initialDirectCost: formData?.changes?.[0]?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					endOfFinancialYear: formData?.endOfFinancialYear,
					advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
					grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
					cashIncentiveAmount: formData?.cashIncentiveAmount_new ?? 0,
					changes: normalizedEffectiveChanges?.map((item, index) => {
						if (index === normalizedEffectiveChanges.length - 1) {
							return {
								...item,
								inputChangeAdvance: formData?.advanceAmounts_new?.reduce((acc, curr) => acc + curr, 0),
								inputChangeAdvanceAmounts: formData?.advanceAmounts_new,
								inputChangeGrossRents: formData?.grossRents_new,
								upfrontLeasePayment: String(formData?.upfrontLeasePayment_new),
								initialDirectCost: String(formData?.initialDirectCost_new),
								securityDeposit: String(formData?.securityDeposit_new),
								cashIncentiveAmount: String(formData?.cashIncentiveAmount_new ?? 0),
								inputChangeDiscountRate: formData?.initialDiscountRate_new,
							};
						}
						return {
							...item,
						};
					}),
					grossRent: 0,
				};

				if (
					Array.isArray(payloadData.grossRents) &&
					Array.isArray(payloadData.advanceAmounts) &&
					payloadData.grossRents.length > payloadData.advanceAmounts.length
				) {
					const paddedAdvanceAmounts = [...payloadData.advanceAmounts];
					while (paddedAdvanceAmounts.length < payloadData.grossRents.length) {
						paddedAdvanceAmounts.push(0);
					}
					payloadData.advanceAmounts = paddedAdvanceAmounts;
				}
			}

			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};

			try {
				if (Object.keys(payloadData).length !== 0) {
					await getLeaseData(formattedPayload).unwrap();
				}
			} catch (error) {
				console.error('Error in data loading:', error);
			}
		}
	};

	const handleCheckFormValidation = async () => {
		try {
			await checkFormValidation();

			await handleDataLoading();
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill in all required fields in the lease details tab.', toastConfig);
		}
	};

	useEffect(() => {
		if (isV3DataActive && leaseControlDataV3?.results) {
			setMemoizedLeaseControlData(leaseControlDataV3?.results ?? []);
		} else if (leaseControlData?.responseData) {
			setMemoizedLeaseControlData(leaseControlData?.responseData ?? []);
		}
	}, [isV3DataActive, leaseControlDataV3?.results, leaseControlData?.responseData, isLeaseTableDataLoading, isLeaseControlDataLoading]);

	useEffect(() => {
		if (!isV3DataActive || selectedStatuses.length === 0) {
			setFilteredDataForTable(memoizedLeaseControlData);
		} else {
			const isUnprocessedSelected = selectedStatuses.includes('Unprocessed');

			const filterData = memoizedLeaseControlData.filter((item) => {
				const itemStatus = item.processStatus;

				if (isUnprocessedSelected && (itemStatus === '' || itemStatus === null || itemStatus === undefined)) {
					return true;
				}

				return selectedStatuses.includes(itemStatus);
			});

			setFilteredDataForTable(filterData);
		}
	}, [isV3DataActive, memoizedLeaseControlData, selectedStatuses]);

	useEffect(() => {
		setSelectedStatuses([]);
	}, [isV3DataActive]);

	useEffect(() => {
		setIsClicked(false);
	}, [screen, viewData?.data?.leaseId]);

	useEffect(() => {
		if (!hasLoadedOnce.current) {
			hasLoadedOnce.current = true;
			handleDataLoading();
		}
	}, []);

	useEffect(() => {
		queryV3Loader();
	}, []);

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', flexDirection: 'column' }}>
			<SectionCard sx={{ padding: '16px', borderRadius: '10px', border: '1px solid #e6ebf2' }}>
				<Spin spinning={isLeaseControlDataLoading}>
					<Box
						sx={{
							gap: '4px',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
						}}
					>
						{screen !== 'VIEW' && (
							<>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
									<Button
										type="primary"
										loading={isLeaseControlDataLoading}
										style={{ marginBottom: 10 }}
										onClick={() => {
											setFlag({ ...flag, check: 1 });

											hasLoadedOnce.current = false;

											if (screen === 'EDIT') {
												handleCheckFormValidation();
											} else {
												handleDataLoading();
											}
										}}
									>
										{screen === 'CREATE' ? 'Run Lease Schedule' : 'Update Lease Schedule'}
									</Button>
								</Box>
							</>
						)}
					</Box>

					<Spin spinning={isLeaseTableDataLoading || isLeaseControlDataLoading}>
						{memoizedLeaseControlData?.length > 0 && (
							<>
								<LeaseModifyScheduleTable
									screen={screen}
									isClicked={isClicked}
									viewData={statusData}
									queryV3Loader={queryV3Loader}
									hasGlAssigned={hasGlAssigned}
									hasCCAssigned={hasCCAssigned}
									leaseControlData={filteredDataForTable}
								/>
							</>
						)}
					</Spin>
				</Spin>
			</SectionCard>
		</Box>
	);
};

export default LeaseRecordManagementV9ModifySchedule;
